import type { RiftboundCard } from '../types';
import { setReleaseDate } from '../constants';
import { foldCardNameForMatch } from './foldCardNameForMatch';
import { stripTrailingPrintSuffix } from './cardPrintSuffix';
import {
  cardOriginSet,
  isAlternateArtCard,
  isOvernumberedCard,
  CARD_PRIZE_STAMP_LABELS,
  cardPrizeStamp,
  isPromoCard,
  isPromoSetCode,
  isSignedCard,
  isStandardSearchVariant,
} from './cardPrintVariant';
import { collectorNumbersEqual } from '../../shared/cardCollectorNumber.js';
import { cardCollectorNumber, formatCardNumber, formatPrintedCollectorLine } from './cardUtils';

const SIGNED_SUFFIX_RE = /\s*[（(]\s*Signed\s*[）)]\s*$/i;
const SERIAL_SUFFIX_RE = /\s*[（(]\s*Serial(?:\s+Numbered)?\s*[）)]\s*$/i;

/** Base title used to group alt-art / signed / promo printings of one card. */
export function cardPrintGroupKey(name: string): string {
  const stripped = stripTrailingPrintSuffix(name).replace(SIGNED_SUFFIX_RE, '').trim();
  return foldCardNameForMatch(stripped);
}

export function isSamePrintGroup(a: Pick<RiftboundCard, 'name'>, b: Pick<RiftboundCard, 'name'>): boolean {
  const left = cardPrintGroupKey(a.name);
  const right = cardPrintGroupKey(b.name);
  return Boolean(left) && left === right;
}

/** `OGN-042`, `OGN-303S`, `UNL-051A`. Prefers riftbound_id serial tokens. */
export function cardPrintCode(
  card: Pick<RiftboundCard, 'setCode' | 'number' | 'riftboundId'>,
): string {
  const set = String(card.setCode || '').trim().toUpperCase();
  const num = cardCollectorNumber(card) || String(card.number || '').trim();
  if (!set && !num) return '';
  if (!num) return set;
  const formatted = formatCardNumber(num);
  return set ? `${set}-${formatted.toUpperCase()}` : formatted.toUpperCase();
}

/**
 * Collector code Rift Atlas puts in brackets: `OGN-039`, `OGN-007A`, `RAD-R05A`.
 * Empty when the card has no set and collector number.
 */
export function riftAtlasPrintCode(card: {
  name?: string;
  setCode?: string;
  number?: string | number | null;
  riftboundId?: string | null;
  originSet?: string | null;
}): string {
  const fromId = String(card.riftboundId || '').trim().split('-')[0] ?? '';
  const catalogSet = String(card.setCode || fromId).trim().toUpperCase();
  const origin = String(card.originSet || '').trim().toUpperCase();
  const set = isPromoSetCode(catalogSet) && origin ? origin : catalogSet;
  const collector = cardCollectorNumber(card);
  if (!set || !collector) return '';
  let token = padAtlasCollector(collector);
  if (card.name && isAlternateArtCard({ name: card.name }) && !/[a-z]$/i.test(token)) {
    token += 'A';
  }
  return `${set}-${token.toUpperCase()}`;
}

/** Zero-pad a numeric collector (`7a` → `007A`). Leave `R05A` as-is. */
function padAtlasCollector(num: string): string {
  const match = num.match(/^(\d+)([a-z]*)$/i);
  if (!match) return num;
  return `${match[1].padStart(3, '0')}${match[2]}`;
}

function openriftMarkers(card: Pick<RiftboundCard, 'raw'>): string[] {
  const raw = card.raw as Record<string, unknown> | undefined;
  const openrift = raw?.openrift as Record<string, unknown> | undefined;
  const markers = openrift?.markers;
  if (!Array.isArray(markers)) return [];
  return markers.map((m) => String(m).toLowerCase());
}

export function isSerialNumberedCard(
  card: Pick<RiftboundCard, 'name' | 'number' | 'riftboundId' | 'raw'>,
): boolean {
  if (SERIAL_SUFFIX_RE.test(card.name ?? '')) return true;
  const markers = openriftMarkers(card);
  if (markers.some((m) => m.includes('serial'))) return true;
  const haystack = [card.number, card.riftboundId].filter(Boolean).join(' ');
  return /[*★]/.test(haystack);
}

export function cardPrintLabels(
  card: Pick<
    RiftboundCard,
    | 'name'
    | 'number'
    | 'riftboundId'
    | 'setCode'
    | 'cardType'
    | 'signature'
    | 'supertype'
    | 'alternateArt'
    | 'raw'
  >,
): string[] {
  const labels: string[] = [];
  if (isStandardSearchVariant(card)) labels.push('Standard');
  if (isAlternateArtCard(card)) labels.push('Alt Art');
  if (isOvernumberedCard(card)) labels.push('Overnumbered');
  if (isSignedCard(card)) labels.push('Signed');
  if (isSerialNumberedCard(card)) labels.push('Serial Numbered');
  if (isPromoCard(card)) labels.push('Promo');
  const stamp = cardPrizeStamp(card);
  if (stamp) labels.push(CARD_PRIZE_STAMP_LABELS[stamp]);
  return labels;
}

/** Labels already implied by the printed collector line, so they stay off the row. */
const VARIANT_ROW_OMITTED_LABELS = new Set(['Standard', 'Alt Art', 'Promo']);

/**
 * Set code shown on a Variants row. Organized-play promos print the parent
 * set (VEN, RAD, …), not the catalog code OPP.
 */
function variantSetCode(
  card: Pick<RiftboundCard, 'setCode' | 'originSet' | 'raw'>,
): string {
  const catalog = String(card.setCode || '').trim().toUpperCase();
  if (catalog === 'OPP') return cardOriginSet(card) || catalog;
  return catalog;
}

/** Visible Variants-row text, used as the alphabetical tie-break. */
function variantSortLabel(card: RiftboundCard): string {
  const line =
    formatPrintedCollectorLine(card) ||
    cardPrintCode(card) ||
    formatCardNumber(card.number != null ? String(card.number) : undefined);
  const labels = cardPrintLabels(card).filter((label) => !VARIANT_ROW_OMITTED_LABELS.has(label));
  return labels.length > 0 ? `${line} · ${labels.join(' · ')}` : line;
}

/**
 * Oldest set first. Same release date (or no date) falls through to the
 * printed collector line. Sets without a release date sort after dated ones.
 */
export function sortPrintings(cards: RiftboundCard[]): RiftboundCard[] {
  return [...cards].sort((a, b) => {
    const dateA = setReleaseDate(variantSetCode(a));
    const dateB = setReleaseDate(variantSetCode(b));
    if (dateA && dateB && dateA !== dateB) return dateA.localeCompare(dateB);
    if (dateA && !dateB) return -1;
    if (!dateA && dateB) return 1;
    const label = variantSortLabel(a).localeCompare(variantSortLabel(b), undefined, {
      sensitivity: 'base',
    });
    if (label !== 0) return label;
    return cardPrintCode(a).localeCompare(cardPrintCode(b));
  });
}

/** Deck slots omit rarity. Keep the catalog row when it is the one that has it. */
function preferPrinting(seed: RiftboundCard, catalog: RiftboundCard): RiftboundCard {
  if (seed.rarity?.trim() || seed.originRarity) return seed;
  if (catalog.rarity?.trim() || catalog.originRarity) return catalog;
  return seed;
}

function openriftShortCollector(card: Pick<RiftboundCard, 'raw'>): string {
  const raw = card.raw as Record<string, unknown> | undefined;
  const openrift = raw?.openrift as Record<string, unknown> | undefined;
  const short = String(openrift?.shortCode || '');
  const match = short.match(/^[A-Z0-9]+-([RT]?\d+[a-z]?)/i);
  return match ? match[1].toLowerCase() : '';
}

/** Collector number for duplicate checks. A short code can restore a dropped letter (`R06` vs `R06b`). */
function printingCollector(card: RiftboundCard): string {
  const stored = cardCollectorNumber(card).toLowerCase();
  const fromShort = openriftShortCollector(card);
  if (
    fromShort &&
    stored &&
    fromShort !== stored &&
    fromShort.startsWith(stored) &&
    /[a-z]$/.test(fromShort)
  ) {
    return fromShort;
  }
  const embedded = stored.match(/^([rt]\d+[a-z]*)-\d+$/i);
  return embedded ? embedded[1].toLowerCase() : stored;
}

function isDistinctFinish(card: RiftboundCard): boolean {
  const id = String(card.riftboundId || '').toLowerCase();
  if (/-md(?:-|$)/.test(id) || /-m(?:-|$)/.test(id)) return true;
  return /\(\s*metal/i.test(card.name || '');
}

/**
 * OpenRift / Riftcodex row of a printing the gallery already has.
 * Prize stamps and metal finishes stay; they are different cards.
 */
export function isRedundantPromoPrinting(
  card: RiftboundCard,
  siblings: readonly RiftboundCard[],
): boolean {
  if (!isPromoCard(card) || cardPrizeStamp(card) || isDistinctFinish(card)) return false;
  const collector = printingCollector(card);
  if (!collector) return false;
  const origin = (cardOriginSet(card) || '').toUpperCase();
  return siblings.some((other) => {
    if (other.id === card.id || isPromoCard(other)) return false;
    if (!collectorNumbersEqual(printingCollector(other), collector)) return false;
    if (!origin) return true;
    return String(other.setCode || '').toUpperCase() === origin;
  });
}

/** Keep catalog hits that are print variants of `card`, always including `card`. */
export function filterSiblingPrintings(card: RiftboundCard, candidates: RiftboundCard[]): RiftboundCard[] {
  const byId = new Map<string, RiftboundCard>();
  byId.set(card.id, card);
  for (const candidate of candidates) {
    if (!candidate.id) continue;
    if (candidate.id !== card.id && !isSamePrintGroup(card, candidate)) continue;
    byId.set(
      candidate.id,
      candidate.id === card.id ? preferPrinting(card, candidate) : candidate,
    );
  }
  const rows = [...byId.values()];
  return sortPrintings(rows.filter((candidate) => !isRedundantPromoPrinting(candidate, rows)));
}
